/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OnOtpVerified, OtpFlow } from '../types';
import { usePhoneNumberUpdateFlow } from '../../usePhoneNumberUpdateFlow';

export interface UsePhoneChangeOtpFlowOptions {
  displayPhoneNumber?: string;
  displayDialingCode?: string;
  startTimer: () => void;
  onVerified?: OnOtpVerified;
}

export function usePhoneChangeOtpFlow({
  displayPhoneNumber,
  displayDialingCode,
  startTimer,
  onVerified,
}: UsePhoneChangeOtpFlowOptions): OtpFlow {
  const { t } = useTranslation('account');
  const [localErrorMessage, setLocalErrorMessage] = useState<string>();
  const phoneNumberUpdateFlow = usePhoneNumberUpdateFlow();

  const submitOtp = useCallback(
    (otp: string) => phoneNumberUpdateFlow.verifyNewPhoneOtp(otp, onVerified),
    [phoneNumberUpdateFlow, onVerified],
  );

  const requestOtp = useCallback(() => {
    if (!displayPhoneNumber || !displayDialingCode) {
      setLocalErrorMessage(t('verifyPhonePage.missingPhoneNumber'));
      return;
    }
    phoneNumberUpdateFlow.requestNewPhoneOtp(
      displayDialingCode,
      displayPhoneNumber,
      startTimer,
      startTimer,
    );
  }, [
    displayPhoneNumber,
    displayDialingCode,
    phoneNumberUpdateFlow,
    startTimer,
    t,
  ]);

  const clearError = useCallback(() => {
    setLocalErrorMessage(undefined);
    phoneNumberUpdateFlow.clearError();
  }, [phoneNumberUpdateFlow]);

  return {
    submitOtp,
    requestOtp,
    errorMessage: localErrorMessage ?? phoneNumberUpdateFlow.errorMessage,
    errorCode: localErrorMessage ? undefined : phoneNumberUpdateFlow.errorCode,
    isLoading: phoneNumberUpdateFlow.isLoading,
    clearError,
  };
}
